import { useEffect } from 'react';
import { getSiteUrl, SITE_NAME } from '@/lib/seo';

export interface SeoMetaConfig {
  title: string;
  description?: string;
  keywords?: string;
  path?: string;
  type?: 'website' | 'article';
  image?: string;
  noindex?: boolean;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function upsertLink(rel: string, href: string) {
  const selector = `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function usePageMeta(config: SeoMetaConfig) {
  const { title, description, keywords, path, type = 'website', image, noindex } = config;

  useEffect(() => {
    const base = getSiteUrl();
    const url = path && path !== '/' ? `${base}${path}` : base;

    document.title = title;

    if (description) upsertMeta('name', 'description', description);
    else removeMeta('name', 'description');

    if (keywords) upsertMeta('name', 'keywords', keywords);
    else removeMeta('name', 'keywords');

    if (noindex) upsertMeta('name', 'robots', 'noindex, nofollow');
    else removeMeta('name', 'robots');

    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', title);
    if (description) upsertMeta('property', 'og:description', description);
    else removeMeta('property', 'og:description');
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    if (image) upsertMeta('property', 'og:image', image);
    else removeMeta('property', 'og:image');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    if (description) upsertMeta('name', 'twitter:description', description);
    else removeMeta('name', 'twitter:description');
    if (image) upsertMeta('name', 'twitter:image', image);
    else removeMeta('name', 'twitter:image');
  }, [title, description, keywords, path, type, image, noindex]);
}